// Collar cut-time step — feed/speed + handling-time inputs on the left,
// a live cut-time summary on the right.
//
// Re-runs `calcCollarNesting` with the same collar inputs as the Nesting
// step so parts-per-sheet stays consistent. Pulls material thickness from
// `part.materialCost.snapshotSource` is not the right place — v1 stored
// thickness on `part.stock.thickness`, which v2 hasn't formalized yet.
// Fallback: 0.5". A future Material-step revamp should expose thickness
// as a first-class number on Part; until then this is what we have.

import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type { CollarSpec } from '@/shared/crmTypes';
import { calcCollarCutTime, calcCollarNesting, COLLAR_DEFAULTS } from '@/shared/collarMath';

const ACCENT = '#0891B2';
/** v1 stored thickness on `part.stock.thickness`; v2 has no canonical home
 *  for it yet, so the cut-time estimate falls back to a typical phenolic
 *  collar thickness. Wire to the real field when the Material step gains a
 *  thickness slot. */
const FALLBACK_THICKNESS_IN = 0.5;

export function CollarCutStep({ part, onChange }: StepProps) {
  const collar: Partial<CollarSpec> = part.collar ?? {};

  const outsideLen = collar.outsideLen ?? 30;
  const outsideWid = collar.outsideWid ?? 10;
  const frameWidth = collar.frameWidth ?? 2;
  const sheetLen = collar.sheetLen ?? COLLAR_DEFAULTS.sheetLen;
  const sheetWid = collar.sheetWid ?? COLLAR_DEFAULTS.sheetWid;
  const spacing = collar.spacing ?? COLLAR_DEFAULTS.spacing;
  const feedIpm = collar.feedIpm ?? COLLAR_DEFAULTS.feedIpm;
  const doc = collar.doc ?? COLLAR_DEFAULTS.doc;
  const loadUnloadMin = collar.loadUnloadMin ?? COLLAR_DEFAULTS.loadUnloadMin;
  const screwDownMin = collar.screwDownMin ?? COLLAR_DEFAULTS.screwDownMin;
  const timeUnitHours = !!collar.timeUnitHours;

  // Thickness fallback — see top-of-file note.
  const matThk = FALLBACK_THICKNESS_IN;

  function patch(updates: Partial<CollarSpec>) {
    onChange({
      collar: {
        outsideLen,
        outsideWid,
        frameWidth,
        ...collar,
        ...updates,
      },
    });
  }

  const nest = calcCollarNesting(outsideLen, outsideWid, sheetLen, sheetWid, spacing);
  const t = calcCollarCutTime({
    outsideLen,
    outsideWid,
    frameWidth,
    feedIpm,
    doc,
    materialThickness: matThk,
  });

  const cutPerSheet = t.cutCombinedPerPart * nest.count;
  const totalPerSheet = cutPerSheet + loadUnloadMin + screwDownMin;
  const qty = part.qtyTiers?.[0] ?? 1;
  const sheetsNeeded = nest.count > 0 ? Math.ceil(qty / nest.count) : 0;
  const totalJob = totalPerSheet * sheetsNeeded;

  function fmtTime(min: number): string {
    if (timeUnitHours) return `${(min / 60).toFixed(2)} hr`;
    return `${min.toFixed(2)} min`;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* ─────────── LEFT: inputs ─────────── */}
      <div className="space-y-4">
        <section>
          <h3 className="text-[11px] font-semibold text-text-mute uppercase tracking-wide mb-2">
            Feed &amp; speed
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Feed rate (IPM)">
              <NumberInput
                value={feedIpm}
                onChange={(v) => patch({ feedIpm: v ?? feedIpm })}
                step={5}
                min={1}
                unit="IPM"
                numpadTitle="Feed rate (IPM)"
              />
              <Hint>Default {COLLAR_DEFAULTS.feedIpm} IPM (from shop)</Hint>
            </Field>
            <Field label="Depth of cut (in)">
              <NumberInput
                value={doc}
                onChange={(v) => patch({ doc: v ?? doc })}
                step={0.0625}
                numpadStep={0.0625}
                min={0.001}
                unit="in"
                numpadTitle="Depth of cut"
              />
              <Hint>Default {COLLAR_DEFAULTS.doc}" per pass</Hint>
            </Field>
          </div>
          <div className="mt-2 px-3 py-2 bg-bg/60 border border-line rounded text-xs">
            Material thickness <span className="font-mono font-semibold">{matThk.toFixed(3)}"</span>
            {' '}÷ DOC <span className="font-mono font-semibold">{t.doc.toFixed(3)}"</span>
            {' '}= <span className="text-orange font-semibold">{t.passes}</span>
            {' '}pass{t.passes === 1 ? '' : 'es'}
            <span className="block text-text-mute italic mt-0.5 text-[10px]">
              Thickness fallback: 0.5" — wire to a real field when the Material step exposes it.
            </span>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[11px] font-semibold text-text-mute uppercase tracking-wide">
              Handling time
            </h3>
            <label className="flex items-center gap-1.5 text-[11px] text-text cursor-pointer select-none">
              <input
                type="checkbox"
                checked={timeUnitHours}
                onChange={(e) => patch({ timeUnitHours: e.target.checked })}
                className="w-3.5 h-3.5 accent-orange"
              />
              show in hours
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`Load / unload (${timeUnitHours ? 'hr' : 'min'})`}>
              <NumberInput
                value={timeUnitHours ? loadUnloadMin / 60 : loadUnloadMin}
                onChange={(v) => {
                  const raw = v ?? (timeUnitHours ? loadUnloadMin / 60 : loadUnloadMin);
                  patch({ loadUnloadMin: timeUnitHours ? raw * 60 : raw });
                }}
                step={0.5}
                min={0}
                unit={timeUnitHours ? 'hrs' : 'min'}
                numpadTitle={`Load / unload time (${timeUnitHours ? 'hr' : 'min'})`}
              />
              <Hint>
                Default {COLLAR_DEFAULTS.loadUnloadMin} min
              </Hint>
            </Field>
            <Field label={`Screw-down to table (${timeUnitHours ? 'hr' : 'min'})`}>
              <NumberInput
                value={timeUnitHours ? screwDownMin / 60 : screwDownMin}
                onChange={(v) => {
                  const raw = v ?? (timeUnitHours ? screwDownMin / 60 : screwDownMin);
                  patch({ screwDownMin: timeUnitHours ? raw * 60 : raw });
                }}
                step={0.5}
                min={0}
                unit={timeUnitHours ? 'hrs' : 'min'}
                numpadTitle={`Screw-down time (${timeUnitHours ? 'hr' : 'min'})`}
              />
              <Hint>
                Default {COLLAR_DEFAULTS.screwDownMin} min
              </Hint>
            </Field>
          </div>
        </section>
      </div>

      {/* ─────────── RIGHT: live summary ─────────── */}
      <div className="space-y-3">
        <h3 className="text-[11px] font-semibold text-text-mute uppercase tracking-wide">
          Cut time · live
        </h3>

        <div className="border border-line rounded bg-paper overflow-hidden">
          <table className="w-full text-xs">
            <tbody>
              <SummaryRow
                label="Inside perimeter"
                value={`${t.innerPerimeter.toFixed(2)}"`}
                time={fmtTime(t.cutInsidePerPart)}
              />
              <SummaryRow
                label="Outside perimeter"
                value={`${t.outerPerimeter.toFixed(2)}"`}
                time={fmtTime(t.cutOutsidePerPart)}
              />
              <tr className="bg-amber/10 border-t border-amber/30">
                <td className="px-3 py-2 text-xs font-semibold">Combined / part</td>
                <td className="px-3 py-2 text-right text-text-mute"></td>
                <td className="px-3 py-2 text-right font-mono font-semibold">
                  {fmtTime(t.cutCombinedPerPart)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="border border-line rounded p-3 bg-paper space-y-1.5 text-sm">
          <Row label="Parts per sheet">
            <span className="font-mono">{nest.count}</span>
          </Row>
          <Row label="Cut time / sheet">
            <span className="font-mono">{fmtTime(cutPerSheet)}</span>
          </Row>
          <Row label={`+ load/unload + screw-down`}>
            <span className="font-mono text-text-mute">
              {fmtTime(loadUnloadMin + screwDownMin)}
            </span>
          </Row>
          <div className="border-t border-dashed border-line pt-1.5">
            <Row label="Total / sheet">
              <span className="font-mono font-semibold" style={{ color: ACCENT }}>
                {fmtTime(totalPerSheet)}
              </span>
            </Row>
          </div>
          <div className="border-t border-line pt-1.5">
            <Row label={`Total for qty ${qty} (${sheetsNeeded} sheets)`}>
              <span className="font-mono font-bold text-orange">{fmtTime(totalJob)}</span>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────── tiny components ───────────
interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div className="text-[10px] text-text-mute italic mt-0.5">{children}</div>;
}

interface RowProps {
  label: string;
  children: React.ReactNode;
}

function Row({ label, children }: RowProps) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-mute">{label}</span>
      {children}
    </div>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
  time: string;
}

function SummaryRow({ label, value, time }: SummaryRowProps) {
  return (
    <tr className="border-b border-line last:border-b-0">
      <td className="px-3 py-1.5">{label}</td>
      <td className="px-3 py-1.5 text-right font-mono text-text-mute">{value}</td>
      <td className="px-3 py-1.5 text-right font-mono">{time}</td>
    </tr>
  );
}
